import { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { RequestStatus } from 'types/RequestStatus';

interface useGetReturn<T = any> {
	getData: () => Promise<T | undefined>;
	data: T;
	status: RequestStatus;
	error?: AxiosError<any> | undefined;
}

export const useGet = <T = any>(
	url: string,
	options: any = {},
): useGetReturn<T> => {
	const [status, setStatus] = useState(RequestStatus.UNLOADED);
	const [error, setError] = useState<AxiosError<any>>();
	const [data, setData] = useState<T>();
	const controller = new AbortController();

	const getData = async () => {
		if (!url) return;
		setStatus(RequestStatus.LOADING);
		try {
			const response = await axios.get(url, {
				...options,
				signal: controller.signal,
			});
			setData(response.data);
			setStatus(RequestStatus.SUCCESS);

			return response.data;
		} catch (error) {
			if (axios.isCancel(error)) return;

			const axiosError = error as AxiosError<any>;
			setError(axiosError);

			if (axiosError.response?.status === 403) {
				setStatus(RequestStatus.FORBIDDEN);
			} else if (axiosError.response?.status === 404) {
				setStatus(RequestStatus.NOT_FOUND);
			} else {
				setStatus(RequestStatus.ERROR);
			}
		}
	};

	useEffect(() => () => controller.abort(), []);

	return {
		getData,
		data: data as T,
		error,
		status,
	};
};
